import { z } from "zod";
import { defineStepConfig } from "../../../registry/helpers";
import { WorkflowStepBatchConfigSchema } from "../../../types/batch";

/**
 * A text socket that tolerates a NUMBER on the wire.
 *
 * Customer numbers are all digits in most divisions' tables and text in others,
 * and step inputs ARE parsed through this schema before the handler runs, with a
 * NON-RETRYABLE failure — so one numeric cell would abort a whole page.
 *
 * ⚠️ Wrapper ORDER matters: write `numericText().nullable().optional()`, never
 * `z.preprocess(fn, z.string().nullable().optional())`. Socket-type inference
 * reads only the outermost wrapper, so burying the optional makes the generated
 * socket REQUIRED and fails publish validation on any workflow leaving it
 * unwired.
 */
// The clone is against upsert-vendor.step.ts, and it is the point: this step is
// deliberately its mirror for the customer side — same combo shape, same socket
// conventions, same numericText guard — so the two read as one pattern. Merging
// them would couple the AR and AP ingest contracts to each other.
// fallow-ignore-next-line code-duplication
function numericText(inner: z.ZodString = z.string()) {
  return z.preprocess(
    (value) =>
      typeof value === "number" && Number.isFinite(value)
        ? String(value)
        : value,
    inner,
  );
}

/**
 * Upsert Customer
 *
 * A COMBO leaf node that upserts a Company (`co_companies`) and a Customer
 * (`ar_customers`) from ONE ingest row, downstream of the "Observe DataSource
 * Link" trigger — the same shape as "Upsert Vendor", and a combo for the same
 * reason: `ar_customers.company_id` is a NOT NULL FK, and workflow fanout
 * batching only supports a LEAF step, so a batched company step feeding a
 * batched customer step is rejected at publish. The hand-off happens in-process.
 *
 * IDENTITY
 * --------
 * `origin_id` is `<customerNumber>::<division>`, normalized on both sides:
 * leading zeros stripped from an all-digit number, and the division reduced to
 * its last character. That is exactly what a DMDTRANS sales order carries in
 * `origin_metadata`, which is what lets the replenishment board join an order to
 * its customer without backfilling `customer_id` across millions of rows.
 *
 * The division comes from the observed link, not a column: the per-division
 * source tables carry no division of their own, so it is a property of which
 * table changed. A row whose link resolves to no division is SKIPPED rather than
 * keyed on the customer number alone — the same number means different
 * customers in different divisions.
 *
 * TWO FEEDS, ONE CUSTOMER
 * -----------------------
 * CUST01 carries identity (name), CUST03 carries the credit hold. Both are
 * separate files with their own CDC triggers, and both feed THIS step keyed on
 * the same origin id. The hold sockets are therefore optional, and a row that
 * leaves them unwired says nothing about the hold rather than clearing it — so
 * a CUST01 run cannot erase what CUST03 set. Wire the hold sockets on the CUST03
 * workflow and leave them empty on CUST01's.
 */
export const upsertCustomerStep = defineStepConfig({
  type: "upsert_customer",
  name: "Upsert Customer",
  description:
    "Upserts a Company and a Customer from one ingest row, keyed on <customerNumber>::<division> so sales orders can be joined to their customer. Credit-hold sockets are optional: wire them from CUST03, leave them unwired on CUST01.",
  category: "Transformation",
  libraryModule: "AccountsReceivable",

  inputSchema: z.object({
    dataSourceLink: z
      .object({
        id: z.string().uuid().nullable().optional(),
        dataSourceId: z.string().uuid().nullable().optional(),
      })
      .describe(
        "The observed Data Source Link. Supplies the division — its DataSource's title — which is half the customer's identity.",
      ),
    customerNumber: numericText().describe(
      "Source customer number (AS400 CUSTNO). Zero padding is stripped when it is all digits.",
    ),
    customerName: z
      .string()
      .nullable()
      .optional()
      .describe("Customer name (CUST01 CNAME). Falls back to the origin id."),

    // ── Credit hold (CUST03) ────────────────────────────────────────────────
    // Optional as a set: leaving them unwired means "this feed says nothing
    // about the hold". Wiring them with all-N means "not held" and clears it.
    rejectOrders: z
      .string()
      .nullable()
      .optional()
      .describe(
        "CUST03 CHLDBK. 'Y' puts the customer on credit hold. Wire from the CUST03 feed only.",
      ),
    holdShipping: z
      .string()
      .nullable()
      .optional()
      .describe(
        "CUST03 CHLDSH. 'Y' puts the customer on credit hold. Wire from the CUST03 feed only.",
      ),
    creditStatus: z
      .string()
      .nullable()
      .optional()
      .describe(
        "CUST03 CRSTAT, carried as context on the hold entry. Never triggers a hold on its own — its code space is undocumented.",
      ),
    creditLimit: z
      .number()
      .nullable()
      .optional()
      .describe("CUST03 CLIMIT, carried as context on the hold entry."),
  }),

  outputSchema: z.object({
    companyId: z.string().nullable().describe("The upserted Company"),
    customerId: z.string().nullable().describe("The upserted Customer"),
    onCreditHold: z
      .boolean()
      .describe("The row's flags put this customer on credit hold"),
    skipped: z
      .boolean()
      .describe(
        "No division resolved from the link, so the row cannot be keyed",
      ),
  }),

  configSchema: z.object({
    batch: WorkflowStepBatchConfigSchema.prefault({ size: 500 }),
  }),

  emits: [] as const,

  execution: {
    startToCloseTimeout: "30 minutes",
    retry: { maximumAttempts: 2 },
  },
});
